import { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Save, KeyRound, UserCircle2, MonitorSmartphone } from "lucide-react";
import { DeviceList } from "../components/DeviceList";
import { PushSettings } from "../components/PushSettings";
import { toast } from "sonner";
import { ROLE_LABELS } from "../lib/roles";
import { getThemePref, setThemePref } from "../lib/theme";
import { Moon, Sun, SunMoon } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm";

export default function Profile() {
    const { user, setUser, logout } = useAuth();
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "" });
    const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
    const [saving, setSaving] = useState(false);

    const saveProfile = async () => {
        if (!form.name.trim()) { toast.error("Vyplňte jméno"); return; }
        setSaving(true);
        try {
            const { data } = await api.put("/auth/profile", form);
            setUser(data);
            toast.success("Profil uložen");
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setSaving(false); }
    };

    const [devKey, setDevKey] = useState(0);
    const changePassword = async () => {
        if (pw.new_password.length < 10) { toast.error("Nové heslo musí mít alespoň 10 znaků"); return; }
        if (pw.new_password !== pw.confirm) { toast.error("Nová hesla se neshodují"); return; }
        try {
            await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
            toast.success("Heslo změněno — ostatní zařízení byla odhlášena");
            setDevKey((k) => k + 1);
            setPw({ current_password: "", new_password: "", confirm: "" });
        } catch (e) { toast.error(e.response?.data?.detail || "Změna hesla selhala — zkontrolujte staré heslo"); }
    };

    return (
        <div className="px-10 py-10 max-w-3xl mx-auto animate-fade-in" data-testid="profile-page">
            <div className="mb-8">
                <div className="label-eyebrow mb-2">Účet</div>
                <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Můj profil</h1>
                <p className="text-sm text-slate-600 mt-2">Role: <b>{ROLE_LABELS[user?.role] || user?.role}</b> — roli mění vlastník firmy na stránce Tým.</p>
            </div>

            <div className="bg-white border border-slate-200 mb-6">
                <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center gap-2"><UserCircle2 className="w-4 h-4 text-orange-600" /><span className="font-display font-bold uppercase tracking-wider text-sm">Osobní údaje</span></div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="label-eyebrow block mb-1.5">Jméno *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" className={inputCls} /></div>
                    <div><label className="label-eyebrow block mb-1.5">Telefon</label><input type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" className={inputCls} placeholder="+420 …" /></div>
                    <div className="md:col-span-2"><label className="label-eyebrow block mb-1.5">E-mail (přihlašovací) *</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" className={inputCls} /></div>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                    <button onClick={saveProfile} disabled={saving} data-testid="profile-save" className="inline-flex items-center gap-1 px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-sm"><Save className="w-3.5 h-3.5" /> Uložit</button>
                </div>
            </div>

            <div className="bg-white border border-slate-200">
                <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center gap-2"><KeyRound className="w-4 h-4 text-orange-600" /><span className="font-display font-bold uppercase tracking-wider text-sm">Změna hesla</span></div>
                <ThemeSwitch />
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="label-eyebrow block mb-1.5">Staré heslo</label><input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" className={inputCls} autoComplete="current-password" /></div>
                    <div><label className="label-eyebrow block mb-1.5">Nové heslo</label><input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" className={inputCls} autoComplete="new-password" /></div>
                    <div><label className="label-eyebrow block mb-1.5">Nové heslo znovu</label><input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" className={inputCls} autoComplete="new-password" /></div>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                    <button onClick={changePassword} data-testid="pw-save" className="inline-flex items-center gap-1 px-5 py-2 text-xs uppercase tracking-wider font-semibold border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm"><KeyRound className="w-3.5 h-3.5" /> Změnit heslo</button>
                </div>
            </div>
            <div className="bg-white border border-slate-200 mt-8" data-testid="my-devices-card">
                <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center gap-2"><MonitorSmartphone className="w-4 h-4 text-orange-600" /><span className="font-display font-bold uppercase tracking-wider text-sm">Moje zařízení</span></div>
                <p className="px-6 pt-3 text-xs text-slate-500">Můžete být přihlášeni na více zařízeních najednou. Relace vydrží 30 dní od posledního použití; odhlásí se jen tlačítkem, změnou hesla nebo zrušením zde.</p>
                <DeviceList key={devKey} onLoggedOutSelf={() => logout()} />
            </div>
            <PushSettings />
        </div>
    );
}

function ThemeSwitch() {
    const [pref, setPref] = useState(getThemePref());
    const opts = [["auto", "Automaticky", SunMoon], ["light", "Světlý", Sun], ["dark", "Tmavý", Moon]];
    return (
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50" data-testid="theme-switch">
            <div className="label-eyebrow mb-2">Vzhled aplikace</div>
            <div className="flex gap-2 flex-wrap">
                {opts.map(([k, l, I]) => (
                    <button key={k} onClick={() => { setThemePref(k); setPref(k); }} data-testid={`theme-${k}`}
                            className={`inline-flex items-center gap-2 min-h-[44px] px-4 text-xs font-bold uppercase tracking-wider border rounded-sm ${pref === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                        <I className="w-4 h-4" /> {l}
                    </button>
                ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">„Automaticky" se řídí nastavením telefonu či počítače. Volba se pamatuje na tomto zařízení.</p>
        </div>
    );
}
